import type { DownloadStageContext } from './atom';

/**
 * The download journey's checkout step (usage → /subscribe) is a full page
 * navigation, so the in-memory `downloadStageContextAtom` — which is also
 * cleared on stage close — doesn't survive the round trip. To reopen the
 * payment-confirmation fallback (`download.ready`) seeded with the asset the
 * user was downloading (DEV-6516), the intent is stashed in `sessionStorage`
 * before the redirect and read back on the `/subscribe` success screen.
 *
 * `sessionStorage` (not `localStorage`) so the intent is scoped to the tab
 * and evaporates if the user abandons checkout and closes the tab. Within the
 * tab, `usePostPaymentDownload` clears it on any page load outside
 * `/subscribe`, so a dropped journey can't resurface the prompt after a
 * later, unrelated checkout (DEV-6727).
 */
const STORAGE_KEY = 'ub:pending-download-intent';

/** A persisted intent always carries the source asset and origin page. */
export type PendingDownloadIntent = DownloadStageContext & {
  sourceAsset: NonNullable<DownloadStageContext['sourceAsset']>;
  returnTo: string;
};

export const savePendingDownloadIntent = (intent: PendingDownloadIntent) => {
  try {
    sessionStorage.setItem(STORAGE_KEY, JSON.stringify(intent));
  } catch {
    // sessionStorage can throw (private mode, quota, SSR). The fallback modal
    // simply won't reopen — not worth surfacing to the user.
  }
};

export const readPendingDownloadIntent = (): PendingDownloadIntent | null => {
  try {
    const raw = sessionStorage.getItem(STORAGE_KEY);
    if (!raw) {
      return null;
    }
    const parsed = JSON.parse(raw) as PendingDownloadIntent;
    // Guard against a partially-written / stale blob: without the source
    // asset there's nothing to download, so treat it as absent.
    return parsed.sourceAsset ? parsed : null;
  } catch {
    return null;
  }
};

export const clearPendingDownloadIntent = () => {
  try {
    sessionStorage.removeItem(STORAGE_KEY);
  } catch {
    // Ignore — see savePendingDownloadIntent.
  }
};
